import { useEffect, useRef, useState } from 'react';
import { api, auswahllisten, baustelle, darf, datum, type Anmelder, type Auswahllisten } from '../api.ts';
import {
  Auswahl, Dialog, Fehlerkasten, Haken, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import Tiefbauplanung from './Tiefbauplanung.tsx';
import Lageplankarte from './Lageplankarte.tsx';
import Bruchtext from '../teile/Bruchtext.tsx';
import BauakteWeg from '../teile/BauakteWeg.tsx';

/**
 * Auftrag vorbereiten.
 *
 * ORIGINALTEXT MARVIN (30.07.2026)
 *   „wir bauen einen neuen menüpunkt: 'Auftrag vorbereiten', das ist als Modal
 *    dann der Lageplan und die wichtigen daten auf der zweiten seite für den
 *    Tiefbau. somit bereiten wir die daten vor, die zum versand notwendig sind.
 *    die gemeinde wird nochmal gegengeprüft. das ist ein abwasch."
 *
 * ORIGINALTEXT MARVIN (24.08.2026)
 *   „auftrag vorbereiten machen wir weiter mit Tiefbau berechnen zusammen."
 *
 * DREI SCHRITTE, und der dritte ist freiwillig:
 *   1  Lageplan   LAP_Maps, geht als Anlage an die Gemeinde
 *   2  Daten      die Felder für AAZ und VAO
 *   3  Planung    der Tiefbauaufwand, NUR INTERN
 *
 * Marvin am 21.08.2026, als die Planung noch unter Seite 1 klebte:
 *   „du musst die planung als 3 punkt optional oben hinzufügen, denn so, kann
 *    ich nicht zurückspringen."
 *
 * SPEICHERN IST GESPERRT, SOLANGE EINE PFLICHTANGABE FEHLT, und der Knopf sagt
 * WELCHE. Marvin am 05.08.2026:
 *   „unten das es vollständig ist ist viel zu unscheinbar, das fällt gar nicht
 *    auf. ich schlage vor, wenn die daten nicht alle eingetragen sind kann man
 *    nicht speichern und es ist ausgegraut."
 * Ein gesperrter Knopf ohne Begründung wäre die zweitschlimmste Lösung: man
 * sieht, dass es nicht geht, aber nicht warum, und sucht im ganzen Formular.
 *
 * EIN FEHLENDER LAGEPLAN SPERRT NICHT. Er wird gemeldet, mehr nicht.
 */

const SEITEN = [
  { nr: 1, name: 'Lageplan' },
  { nr: 2, name: 'Daten für den Tiefbau' },
  { nr: 3, name: 'Planung', zusatz: '(optional)' },
];

export default function Vorbereiten({
  ich, auftragId, zu, fertig, start,
}: {
  ich: Anmelder; auftragId: string; zu: () => void; fertig: () => void;
  /* AUF WELCHER SEITE DAS FENSTER AUFGEHT.
   *
   * Marvin, 08.09.2026: „planung öffnen soll auf nur auf den bereich
   * verweisen, nicht auf den gesamten popup «Auftrag vorbereiten»."
   *
   * Der Weg aus der Karte „Tiefbauaufwand" heisst „Planung öffnen" und landete
   * auf Seite 1, dem Lageplan für die Gemeinde. **Ein Weg, der etwas anderes
   * verspricht als er tut, kostet zwei Klicks und einmal Suchen** - dieselbe
   * Lehre wie bei den Chips der Blöcke Kunde, Technik und Verwaltung am
   * 31.08.2026, die alle auf demselben Anfang derselben Rolle landeten.
   *
   * Die Zahl steht am AUFRUFER und nicht im Fenster: welcher Bereich gemeint
   * ist, weiss die Karte, aus der der Weg kommt. */
  start?: number;
}) {
  const [d, setD] = useState<any>(null);
  const [seite, setSeite] = useState(
    SEITEN.some((s) => s.nr === start) ? (start as number) : 1);
  const [werte, setWerte] = useState<Record<string, string>>({});
  const [kreuze, setKreuze] = useState<Record<string, boolean>>({});
  const [offen, setOffen] = useState(false);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  const [gespeichert, setGespeichert] = useState<string | null>(null);

  async function laden() {
    try {
      const r = await api.vorbereiten(auftragId);
      setD(r); setWerte(r.werte); setKreuze(r.kreuze.wirksam);
      setOffen(false); setFehler(null);
    } catch (f) { setFehler(ausFehler(f)); }
  }
  /* HIER STEHT KEIN `useFrisch`, UND DAS IST EINE ENTSCHEIDUNG.
     `laden()` setzt `werte` und `kreuze` auf den Stand des Servers zurück.
     Auf diesem Bildschirm SCHREIBT gerade jemand: wer auf Seite 1 speichert
     und auf Seite 2 tippt, verlöre seine Eingabe unter der Hand. **Ein
     Nachzug gehört an einen Bildschirm, der ANZEIGT, und nicht an ein
     Formular, in dem jemand schreibt** - dieselbe Lehre wie bei `useEntwurf`
     (31.08.2026) und beim Fokus im `Dialog` (02.09.2026). Der Bildschirm
     DAHINTER zieht sehr wohl nach: die Akte über `fertig={laden}`, der Chip
     über seinen eigenen `useFrisch`. */
  useEffect(() => { laden(); }, [auftragId]);

  if (!d) {
    return (
      <Dialog titel="Auftrag vorbereiten" laeuft zu={zu} breite={1360}>
        <Fehlerkasten lage={fehler} />
        {!fehler && <p className="klein leise">Wird geladen.</p>}
      </Dialog>
    );
  }

  const a = d.auftrag;
  const setze = (feld: string, wert: string) => {
    setWerte((w) => ({ ...w, [feld]: wert }));
    setOffen(true);
    setGespeichert(null);
  };

  // Die Pflichtangaben werden HIER gerechnet, damit der Knopf sofort mitgeht.
  // Dieselbe Regel wie auf dem Server (regeln/vorbereitung.ts): alle
  // Pflichtfelder ausser der Gruppe Haltverbot.
  const fehlt = (d.felder as any[])
    .filter((f) => f.pflicht && f.gruppe !== 'Haltverbot')
    .filter((f) => !String(werte[f.feld] ?? '').trim())
    .map((f) => f.beschriftung);

  const fertigSeite = (nr: number) =>
    nr === 1 ? d.lageplanDa === true
    : nr === 2 ? fehlt.length === 0
    : !!d.schaetzung;

  /* WEITER HEISST AUCH SPEICHERN (.454). ORIGINALTEXT MARVIN (22.10.2026):
     „wenn ich unten einfach auf die dritte Seite klicke, speichert er die
      Sachen auf der zweiten Seite nicht ... Das bitte umstellen, dass wenn
      ich auch direkt auf Weiter klicke, dass er das auch direkt speichert."
     Gilt für „Weiter zur Planung" und für die Reiter oben: steht auf Seite 2
     Ungespeichertes, wird erst gespeichert, dann gewechselt. Fehlt noch eine
     Pflichtangabe, wird trotzdem gespeichert (der Server nennt, was fehlt). */
  async function wechsle(nr: number) {
    if (seite === 2 && offen && nr !== 2) {
      const ok = await speichern();
      if (!ok) return;
    }
    /* Von Seite 1 kommend frisch laden: was eben gezeichnet wurde, steht
       sonst auf Seite 2 noch nicht als Vorschlag (Meter, Gruben). */
    if (seite === 1 && nr === 2 && !offen) await laden();
    setSeite(nr);
  }

  async function speichern(): Promise<boolean> {
    setLaeuft(true); setFehler(null);
    try {
      const alles: Record<string, string> = { ...werte };
      for (const [k, v] of Object.entries(kreuze)) alles[k] = v ? 'ja' : 'nein';
      const r = await api.vorbereitenSpeichern(auftragId, alles);
      setGespeichert(r.klartext);
      setOffen(false);
      await laden();
      fertig();
      return true;
    } catch (f) { setFehler(ausFehler(f)); return false; }
    finally { setLaeuft(false); }
  }

  return (
    <>
      <Dialog
        titel="Auftrag vorbereiten"
        /* DIE KOMPLETTE ANSCHRIFT MIT KLS UND SM, aus derselben Funktion wie
           in jedem anderen Fenster - `a.ort` heisst hier `ort` und nicht
           `ort_text`, deshalb steht es daneben. Der Kundenname bleibt: auf
           diesem Bildschirm wird ein Papier für die Gemeinde gebaut. */
        untertitel={baustelle({ ...a, ort_text: a.ort })
          + (a.kunde_name ? `, ${a.kunde_name}` : '')}
        laeuft={laeuft} zu={zu} breite={1360}
      >
        <Fehlerkasten lage={fehler} />

        {/* ------------------------------------------------------- Reiter */}
        <div className="schritte">
          {SEITEN.map((s) => (
            <button key={s.nr} onClick={() => { void wechsle(s.nr); }} disabled={laeuft}
                    aria-current={seite === s.nr}
                    className={fertigSeite(s.nr) ? 'fertig' : undefined}>
              <span className="kreis">{s.nr}</span>
              {s.name}
              {s.zusatz && <span className="leiser"> {s.zusatz}</span>}
            </button>
          ))}
        </div>

        <div className="spalten">
          <div className="spalte">
            {seite === 1 && <SeiteLageplan d={d} auftragId={auftragId}
                                           weiter={() => { void wechsle(2); }}
                                           neuLaden={laden} />}
            {seite === 2 && (
              <SeiteDaten d={d} werte={werte} setze={setze} ich={ich} auftragId={auftragId}
                          typGeaendert={() => { fertig(); }}
                          kreuze={kreuze} setKreuze={(k: any) => {
                            setKreuze(k); setOffen(true); setGespeichert(null); }}
                          fehlt={fehlt} />
            )}
            {seite === 3 && (
              /* NACH DEM ÜBERNEHMEN LÄDT AUCH DIE AKTE DAHINTER.
                 ---------------------------------------------------------
                 ORIGINALTEXT MARVIN (08.09.2026)
                   „Tiefbauaufwand erscheint erst nach Neuladen -
                    automatischer Push statt Seite aktualisieren"

                 Hier stand `neuLaden={laden}`, also nur DIESES Fenster.
                 Der Aufwand steht aber in der AKTE, und die hatte niemand
                 angerührt: er stand dort erst nach dem Neuladen der Seite.
                 **Ein Wert, der geschrieben ist und auf dem Bildschirm
                 dahinter nicht steht, sieht aus wie einer, der nicht
                 angekommen ist** - und dann übernimmt jemand ein zweites
                 Mal. Beides zusammen ist die Antwort: das Fenster braucht
                 den neuen Stand für seine eigenen Zahlen, die Akte für
                 ihren Block. */
              <SeitePlanung auftragId={auftragId} offen={offen}
                            bauakte={d.bauakte}
                            neuLaden={async () => { await laden(); fertig(); }} />
            )}
          </div>

          {/* DIE RECHTE SPALTE GEHÖRT DER BEHÖRDE UND NICHT DEM TIEFBAU.
              ORIGINALTEXT MARVIN (05.09.2026)
                „hier bei der planung kommt der ganze rechte bereich die zwei
                 blöcke raus, der gehört hie rnicht rein."

              **BEIDE KARTEN BEANTWORTEN FRAGEN ZU SEITE 1 UND 2.** „Gemeinde,
              gegengeprüft" sagt, an welche Behörde der Antrag geht und wie
              lange sie braucht; „Kommt bei der Terminvergabe" sagt, was hier
              ABSICHTLICH nicht gefragt wird. Seite 3 ist die interne
              Tiefbauzeichnung - sie geht in kein Papier an eine Behörde und
              hat mit dem Lageplan nichts zu tun (der Satz steht darüber).
              **Eine Karte, die auf jeder Seite steht, wird auf der Seite, zu
              der sie gehört, nicht mehr gelesen** - und hier nimmt sie der
              Karte 340 Bildpunkte weg, die die Zeichnung braucht. */}
          {seite !== 3 && (
            <div className="spalte schmal">
              {seite === 2 && <AusDerKarte d={d} />}
              <GemeindeKarte d={d} />
              <TerminKarte d={d} />
            </div>
          )}
        </div>

        {/* ------------------------------------------------------- Balken */}
        <div className={'standbalken ' + (gespeichert ? 'gruen' : d.stand.farbe)}>
          <span className="zeichen">
            {gespeichert ? '✓' : d.stand.farbe === 'gruen' ? '✓'
              : d.stand.farbe === 'rot' ? '!' : '…'}
          </span>
          <span>
            <b>{gespeichert ?? (fehlt.length
              ? `Noch nicht vollständig. Es fehlt: ${fehlt.join(', ')}.`
              : d.stand.text)}</b>
            {!gespeichert && d.stand.zusatz && !fehlt.length && (
              <span className="leise"> {d.stand.zusatz}</span>
            )}
          </span>
        </div>

        <div className="reihe" style={{ marginTop: 10 }}>
          {seite === 2 && (
            <button className="knopf haupt" disabled={laeuft || fehlt.length > 0}
                    onClick={() => { void speichern(); }}
                    title={fehlt.length ? `Es fehlt: ${fehlt.join(', ')}` : undefined}>
              {laeuft ? 'Einen Moment'
                : fehlt.length
                  ? `Es fehlt: ${fehlt[0]}${fehlt.length > 1
                      ? ` und ${fehlt.length - 1} weitere` : ''}`
                  : 'Vorbereitung speichern'}
            </button>
          )}
          {seite === 2 && (
            <button className={'knopf' + (gespeichert && !d.schaetzung ? ' haupt' : '')}
                    onClick={() => { void wechsle(3); }} disabled={laeuft}>
              {offen ? 'Speichern und weiter zur Planung' : 'Weiter zur Planung'} &rarr;
            </button>
          )}
          {/* Hier stand ein zweites „Schließen". Es gab das erste im Kopf
              des Fensters schon, und es stand nur deshalb hier, weil der Kopf
              bei einem langen Fenster nach oben aus dem Bild rollte. Seit dem
              25.08.2026 bleibt der Kopf stehen und der Leib rollt. */}
        </div>
      </Dialog>
      {/* HIER STAND DAS ZWEITE FENSTER. Die Planung IST jetzt Seite 3 - siehe
          `SeitePlanung`. Ein Fenster über einem Fenster für dieselbe Sache
          war ein Klick und eine Rückfrage zu viel. */}
    </>
  );
}


// =============================================================== Seite 1

function SeiteLageplan({ d, auftragId, weiter, neuLaden }: any) {
  /* HIER STAND DER DRITTE MESSFÜHLER DES HAUSES.
     ------------------------------------------------------------------------
     Die Spalte der Karte wurde an drei Stellen gemessen: hier, in der
     Tiefbauplanung und - seit .107 - auf der Kartenseite. Dreimal derselbe
     `ResizeObserver`, dreimal `kartenmass`, und zwei davon mit einem anderen
     Wunschmass. Er steht jetzt EINMAL, in `teile/Kartenflaeche.tsx`; hier
     bleibt das Wunschmass, denn das ist eine Aussage über DIESES Fenster. */
  return (
    <>
      {/* DIE KARTE STEHT OBEN, nicht der Befund darüber.
       *
       * ORIGINALTEXT MARVIN (24.08.2026)
       *   „außerdem kann der Lap_Maps nciht erstellt werden, da fehlt alles."
       *
       * Bis hierher konnte dieser Reiter genau eines: NACHSEHEN, ob die Datei
       * schon da ist. Wer sie erzeugen wollte, fand keinen Weg. */}
      <div className="karte">
        <h3>
          Lageplan für den Tiefbau
          <BauakteWeg bauakte={d.bauakte} />
          <span className="weg">
            {d.lageplanDa === null ? 'wird geprüft'
              : d.lageplanDa ? 'LAP_Maps liegt in der Akte'
              : 'LAP_Maps fehlt noch'}
          </span>
        </h3>
        <div className="leib">
          {d.lageplanDa === false && (
            <div className="hinweis fehler">
              Ohne LAP_Maps fehlt die Anlage in der Mail an die Gemeinde, und
              der Tiefbauer fährt ohne Trasse los.
            </div>
          )}
          {/* DIE KARTE MISST IHRE SPALTE, STATT EINE ZAHL ZU TRAGEN. Hier
              stand fest 820 mal 520; die rechte Spalte daneben schrumpft
              nicht, und auf einem nicht ganz aufgezogenen Fenster lief die
              Behördenanschrift rechts aus dem Fenster hinaus. Das Verhältnis
              bleibt: der Ausschnitt im Fenster soll zeigen, was auf dem Blatt
              steht. */}
          <Lageplankarte auftragId={auftragId}
                         breite={820} hoehe={520}
                         aufAenderung={neuLaden} />
          <p className="klein leise" style={{ marginTop: 10, marginBottom: 0 }}>
            Der Lageplan geht an die Behörde und zeigt deshalb nur den
            öffentlichen Teil. Die Planung auf Seite 3 ist etwas anderes: sie
            läuft bis ins Haus und bleibt im Haus. Das Luftbild ist ein
            amtliches Orthophoto der Landesvermessung; die Quellenangabe steht
            auf dem Blatt.
          </p>
        </div>
      </div>

      <div className="karte">
        <h3>Was auf Seite 2 gebraucht wird</h3>
        <div className="leib">
          <p className="klein">
            Antragsteller, Umfang der Arbeiten, wo gegraben wird und der
            Regelplan. Diese Angaben brauchen AAZ und VAO. Liegen sie vor, gehen
            beide automatisch an die Gemeinde, sobald bei der Terminvergabe
            „öffentlich" gewählt wird.
          </p>
          <p className="klein leise">
            Ob öffentlich, privat oder Sammel-VAO, wird hier nicht gefragt: das
            ergibt sich erst im Telefonat mit dem Kunden.
          </p>
          <button className={'knopf' + (d.lageplanDa ? ' haupt' : '')}
                  onClick={weiter} style={{ marginTop: 6 }}>
            Weiter zu Seite 2 &rarr;
          </button>
        </div>
      </div>
    </>
  );
}


// =============================================================== Seite 2

function SeiteDaten({ d, werte, setze, kreuze, setKreuze, fehlt, ich, auftragId, typGeaendert }: any) {
  /* DIE MASSE STEHEN NUR IM MASSBLOCK, nicht noch einmal als einzelne Felder.
   *
   * Sie stehen im Katalog als zwölf gewöhnliche Felder („Gehweg, tatsächliche
   * Breite", „Fahrbahn, Länge", ...). Zwölf Zahlenfelder untereinander, von
   * denen neun nicht zur gewählten Lage gehören, sind kein Formular, sondern
   * eine Wand. Der Block hinter der Lage zeigt genau die drei, die gelten. */
  const massfelder = new Set(Object.values(d.masse.alle).flat() as string[]);
  const felder: any[] = (d.felder as any[]).filter((f) => !massfelder.has(f.feld));
  const gruppen: string[] = [...new Set(felder.map((f) => f.gruppe))]
    .filter((g) => felder.some((f) => f.gruppe === g && f.art !== 'checkbox'));

  return (
    <>
      {d.verworfen.map((v: string, i: number) => (
        <div key={i} className="hinweis warnung">{v}</div>
      ))}

      <AuftragstypKarte a={d.auftrag} ich={ich} auftragId={auftragId} geaendert={typGeaendert} />

      {gruppen.map((g) => (
        <div className="karte" key={g}>
          <h3>{g}</h3>
          <div className="leib">
            {felder.filter((f) => f.gruppe === g && f.art !== 'checkbox')
              .map((f) => (
              <div key={f.feld}>
                <Feld f={f} d={d} werte={werte} setze={setze} fehlt={fehlt} />
                {/* Die Maße stehen direkt hinter der Lage: sie hängen daran. */}
                {f.feld === 'lage' && (
                  <Massblock d={d} werte={werte} setze={setze} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {d.kreuze.felder.length > 0 && (
        <div className="karte">
          <h3>Ankreuzfelder des Vordrucks</h3>
          <div className="leib">
            <div className="hinweis">
              <b>Warum das hier steht</b>
              Diese Kreuze standen auf festen Vorgaben, die einen Gehweg-Fall
              beschreiben, und wurden nie abgefragt. Bei einer Fahrbahn-Maßnahme
              behauptete das Formular deshalb „Arbeitsstelle: Gehweg". Was sich
              aus Lage und Art der Maßnahme ergibt, ist abgeleitet und
              gekennzeichnet; der Rest ist hier änderbar.
            </div>
            <div className="kreuzraster">
              {d.kreuze.felder.map((f: any) => (
                <label key={f.feld}
                       className={f.feld in d.kreuze.abgeleitet ? 'abgeleitet' : ''}>
                  <input type="checkbox" checked={!!kreuze[f.feld]}
                         onChange={(e) => setKreuze({ ...kreuze,
                                                      [f.feld]: e.target.checked })} />
                  {f.beschriftung}
                  {f.feld in d.kreuze.abgeleitet && (
                    <span className="merker">abgeleitet</span>
                  )}
                </label>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Feld({ f, d, werte, setze, fehlt }: any) {
  const wert = werte[f.feld] ?? '';
  const fehltDas = f.pflicht && f.gruppe !== 'Haltverbot' && !String(wert).trim();
  const hinweis = [
    d.hinweise[f.feld],
    fehltDas ? 'Pflichtangabe für AAZ und VAO.' : null,
  ].filter(Boolean).join(' ');

  if (f.art === 'regelplan') {
    return <Regelplanfeld d={d} wert={wert} setze={setze} fehltDas={fehltDas} />;
  }

  if (f.art === 'liste') {
    // Ein gespeicherter Wert, den die Liste nicht mehr kennt, wird VORN
    // eingehängt statt verschluckt. Sonst stünde im Formular etwas anderes als
    // in der Datenbank.
    const liste: string[] = f.liste ?? [];
    const optionen = wert && !liste.includes(wert) ? [wert, ...liste] : liste;
    return (
      <Auswahl name={f.feld} titel={f.beschriftung} breite="100%" wert={wert}
               setze={(v: string) => setze(f.feld, v)}
               leerText="bitte wählen" hinweis={hinweis || undefined}
               optionen={optionen.filter(Boolean).map((o) => ({ wert: o, text: o }))} />
    );
  }
  return (
    <Text name={f.feld} titel={f.beschriftung} breite="100%" wert={wert}
          setze={(v: string) => setze(f.feld, v)}
          hinweis={hinweis || undefined}
          // AUSDRÜCKLICH kein Datumsfeld des Browsers: das liefert JJJJ-MM-TT,
          // und genau so stünde das Datum dann im Schreiben an die Gemeinde.
          platzhalter={f.art === 'datum' ? 'TT.MM.JJJJ' : undefined} />
  );
}

function Massblock({ d, werte, setze }: any) {
  const satz: string[] = d.masse.alle[
    (werte.lage ?? '').toLowerCase() === 'gehweg' ? 'gehweg'
    : (werte.lage ?? '').toLowerCase() === 'fahrbahn' ? 'fahrbahn'
    : (werte.lage ?? '').toLowerCase().startsWith('parkl') ? 'parkluecke'
    : 'sonstiges'];
  return (
    <div className="massblock">
      <div className="klein leise">Maße {werte.lage || 'der Stelle'}</div>
      <div className="feldreihe">
        {satz.map((feld, i) => (
          <Text key={feld} name={feld} titel={d.masse.ueberschrift[i]} breite={150}
                wert={werte[feld] ?? ''} setze={(v: string) => setze(feld, v)} />
        ))}
      </div>
      <p className="klein leise">
        Die Länge ist die Angabe, die in der Anordnung steht. Der Standardwert 1
        ist fast nie richtig.
      </p>
    </div>
  );
}

/* EIN PFLICHTFELD, DAS NICHT WIE EINES AUSSIEHT, IST EINE FALLE.
 *
 * Der Regelplan steht in `NIE_WEGLASSEN` und ist damit Pflicht wie
 * Verkehrsbereich und Ampel - nur hat er das als einziges Feld dieser Seite
 * nicht GESAGT: kein Stern hinter der Beschriftung, kein Satz „Pflichtangabe
 * für AAZ und VAO." darunter. Wer den Bildschirm von oben nach unten
 * ausfüllt, füllt aus, was rot ist; dieses Feld war stumm, und der
 * Speichern-Knopf sagte danach „Es fehlt: Regelplan" an einer Stelle, an der
 * nichts danach aussah.
 *
 * Gefunden hat es Prüfstand 016 (04.09.2026): er füllt genau das aus, was der
 * Bildschirm selbst als Pflicht ausweist - und ist an diesem einen Feld
 * hängengeblieben. **Ein Prüfstand, der einen Weg geht, findet einen stummen
 * Hinweis; einer, der Felder abfragt, findet ihn nie.** */
function Regelplanfeld({ d, wert, setze, fehltDas }: any) {
  const { passend, weitere } = d.regelplaene;
  const alle = passend.length + weitere.length;
  const text = !wert && !passend.length
    ? `Alle ${alle} Regelpläne stehen zur Auswahl. Wähle Verkehrsbereich und `
      + 'Art der Sperrung, dann stehen die passenden oben.'
    : passend.length
      ? `${passend.length} ${passend.length === 1 ? 'Plan passt' : 'Pläne passen'} `
        + `zur Auswahl und ${passend.length === 1 ? 'steht' : 'stehen'} oben. `
        + `Die übrigen ${weitere.length} sind darunter weiterhin wählbar.`
      : `Zu dieser Kombination gibt es keinen Plan. Alle ${alle} sind trotzdem wählbar.`;

  return (
    <div>
      <label className="feld" style={{ display: 'block' }}>
        <span className="titel">
          Regelplan
          {fehltDas && <span className="pflicht" title="Pflichtfeld"> *</span>}
        </span>
        <select id="regelplan" value={wert} style={{ width: '100%' }}
                onChange={(e) => setze('regelplan', e.target.value)}>
          <option value="">bitte wählen</option>
          {passend.length > 0 && (
            <optgroup label="Passt zur Auswahl">
              {passend.map((p: any) => (
                <option key={p.datei} value={p.datei}>
                  {p.anzeige}, {p.beschreibung}
                </option>
              ))}
            </optgroup>
          )}
          {weitere.length > 0 && (
            <optgroup label={passend.length ? 'Weitere Regelpläne' : 'Alle Regelpläne'}>
              {weitere.map((p: any) => (
                <option key={p.datei} value={p.datei}>
                  {p.anzeige}, {p.beschreibung}
                </option>
              ))}
            </optgroup>
          )}
        </select>
        {fehltDas && (
          <div className="feldhinweis">Pflichtangabe für AAZ und VAO.</div>
        )}
      </label>
      <p className="klein leise">{text}</p>
      {d.regelplanBefund && (
        <div className="hinweis warnung">
          <b>Passt das?</b>{d.regelplanBefund} Es hält nichts auf: es gibt
          Fälle, in denen der Innendienst es besser weiß.
        </div>
      )}
    </div>
  );
}


// =============================================================== Seite 3

/**
 * SEITE 3 IST DIE KARTE, NICHT DER WEG DORTHIN.
 *
 * ORIGINALTEXT MARVIN (05.09.2026, Bild 4)
 *   „wenn ich die planung für den tiefbau anklicke, dann soll direkt hier die
 *    karte kommen, so wie bei dem vorbereiten zum tiefbau. ich brsuche keine
 *    erklärung oder nochmal ein popup. das ist unnnötig."
 *
 * **HIER STANDEN DREI DINGE, UND KEINES DAVON WAR DIE KARTE:** eine
 * Zusammenfassung der letzten Schätzung, zwei Karten mit Erklärtext und ein
 * Knopf, hinter dem ein ZWEITES Fenster über dem ersten aufging. Seite 1
 * derselben Reihe macht es seit jeher anders - dort liegt die Karte einfach
 * da -, und **zwei Seiten desselben Fensters, die dieselbe Handbewegung
 * verschieden verlangen, sind ein Bildschirm, den man lernen muss.**
 *
 * DIE ZAHLEN GEHEN DABEI NICHT VERLOREN. Sie stehen in der Planung selbst,
 * rechts neben der Karte, und zwar LAUFEND gerechnet statt als Stand vom
 * letzten Mal. Was hier wegfällt, ist die Abschrift daneben.
 *
 * DER ERKLÄRTEXT FÄLLT WEG, bis auf den einen Satz, der aus der Seite selbst
 * nicht hervorgeht: dass diese Zeichnung im Haus bleibt. Er steht in der
 * Planung, über der Karte. „Sie hält nichts auf" und „wofür sie gut ist" sind
 * Sätze fürs Handbuch - **eine Erklärung, die bei jedem Aufruf dasteht, ist
 * beim ersten Mal eine Auskunft und ab dem zweiten Fläche, die den Blick von
 * der Karte nimmt.** Dieselbe Lehre wie beim Erklärtext über der Abwurffläche
 * am 27.08.2026.
 */
function SeitePlanung({ auftragId, offen, bauakte, neuLaden }: any) {
  return (
    <>
      {/* DIE BAUAKTE STEHT AUCH HIER, und das ist keine zweite Fassung: es
          ist dasselbe Bauteil an der zweiten Stelle, an der Marvin sie
          verlangt hat. Sie steht ÜBER der Zeichenflaeche, weil man sie
          aufschlaegt, bevor man zeichnet - und nicht, wenn man fertig ist. */}
      {bauakte && (
        <div className="reihe" style={{ marginBottom: 8 }}>
          <BauakteWeg bauakte={bauakte} />
        </div>
      )}
      {offen && (
        <div className="hinweis warnung">
          <b>Auf Seite 2 stehen ungespeicherte Angaben</b>
          Speicher erst dort, dann komm zurück. Still zu speichern wäre
          genau die Sorte Hilfsbereitschaft, die man erst merkt, wenn sie
          etwas überschrieben hat.
        </div>
      )}
      <Tiefbauplanung auftragId={auftragId} fertig={neuLaden} />
    </>
  );
}


// ========================================================== Rechte Spalte

function GemeindeKarte({ d }: any) {
  const g = d.gemeinde;
  return (
    <div className="karte">
      <h3>Gemeinde, gegengeprüft</h3>
      <div className="leib">
        {!g ? (
          <div className="hinweis warnung">
            <b>Keine Gemeinde hinterlegt</b>
            Ohne sie geht keine Mail und es fehlt die Anschrift im Anschreiben.
          </div>
        ) : (
          <>
            <table className="tabelle">
              <tbody>
                <tr><td>Gemeinde</td><td><b>{g.name}</b></td></tr>
                <tr><td>Behörde</td><td>{g.verwaltung ?? '-'}</td></tr>
                <tr><td>Anschrift</td>
                    <td>{[g.strasse, g.plz_ort].filter(Boolean).join(', ') || '-'}</td></tr>
                {/* EINE MAILADRESSE IST EIN WORT OHNE BRUCHSTELLE.
                    `aufgrabungsmanagement@stadt.koblenz.de` trägt weder ein
                    Leerzeichen noch einen Bindestrich; ein Browser bricht dort
                    nirgends um, und **das breiteste Wort bestimmt die Breite
                    der Spalte** - deshalb stand auf Marvins Bild auch der Satz
                    zum Vorlauf abgeschnitten da, obwohl der ganz normale
                    Leerzeichen hat. `Bruchtext` erlaubt den Umbruch nach dem
                    Klammeraffen und nach einem Punkt; der Text selbst bleibt
                    Zeichen für Zeichen derselbe. */}
                <tr><td>Empfänger</td>
                    <td>{g.mail_an?.length
                      ? g.mail_an.map((m: string) => (
                          <div key={m}><Bruchtext text={m} /></div>))
                      : '-'}</td></tr>
                {/* DIE ZAHL ALLEIN BEANTWORTET DIE FRAGE NICHT.
                    Marvin, 01.09.2026: „was ist mit der vorlaufzeit der
                    behörden? ist das eingetragen?" Hier stand `vorlauf_tage`
                    und sonst nichts - bei einer leeren Spalte also „ Werktage"
                    und keine Auskunft, obwohl der globale Wert längst greift.
                    Der Satz kommt jetzt vom Server und sagt, WOHER die Zahl
                    stammt. */}
                <tr><td>Vorlauf</td>
                    <td>{g.vorlauf?.satz ?? `${g.vorlauf_tage ?? '-'} Werktage`}
                    </td></tr>
              </tbody>
            </table>
            {d.gemeindeFehlt.length ? (
              <div className="hinweis warnung">
                <b>Im Gemeindeblatt fehlt etwas</b>
                {d.gemeindeFehlt.map((f: string, i: number) => (
                  <div key={i}>{f}</div>
                ))}
              </div>
            ) : (
              <div className="hinweis erledigt">
                <b>Alle Angaben der Gemeinde stehen.</b>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TerminKarte({ d }: any) {
  const a = d.auftrag;
  const BEREICH: Record<string, string> = {
    oeffentlich: 'öffentlicher Grund', privat: 'privater Grund',
    sammel_vao: 'Sammel-VAO',
  };
  return (
    <div className="karte">
      <h3>Kommt bei der Terminvergabe</h3>
      <div className="leib">
        <table className="tabelle">
          <tbody>
            <tr><td>Bereich</td>
                <td>{a.tb_bereich ? BEREICH[a.tb_bereich]
                  : <span className="leise">noch nicht entschieden</span>}</td></tr>
            <tr><td>Tiefbaufirma</td>
                <td>{a.tb_firma ?? <span className="leise">noch nicht vergeben</span>}</td></tr>
            <tr><td>Termin</td>
                <td>{a.tb_datum ? datum(a.tb_datum)
                  : <span className="leise">noch nicht vergeben</span>}</td></tr>
          </tbody>
        </table>
        <p className="klein leise">
          Ob öffentlich, privat oder Sammel-VAO, entscheidet sich im Telefonat
          mit dem Kunden und wird deshalb dort gefragt, nicht hier.
        </p>
      </div>
    </div>
  );
}

function AusDerKarte({ d }: any) {
  const s = d.schaetzung;
  if (!s) return null;
  const z = (n: number) => Number(n ?? 0).toFixed(1).replace('.', ',');
  return (
    <div className="karte">
      <h3>Aus der Planung</h3>
      <div className="leib">
        <table className="tabelle">
          <tbody>
            <tr><td>Grabenlänge</td><td>{z(s.meter)} m</td></tr>
            <tr><td>Montagegruben</td><td>{s.gruben}</td></tr>
          </tbody>
        </table>
        <p className="klein leise">
          Anhaltspunkt aus Seite 3. Sie geht nicht in den Antrag.
        </p>
      </div>
    </div>
  );
}


/**
 * DER AUFTRAGSTYP IN DER VORBEREITUNG (.239).
 *
 * ORIGINALTEXT MARVIN (28.09.2026, Bild der Auftragsliste, Spalte
 * „Auftragstyp" mit „fehlt")
 *   „ich will gerne, dass der "Auftragstyp" mit auch zur auswahl bei
 *    "Auftrag vorbereiten" mit rein kommt."
 *
 * DIESELBE AUSWAHL WIE IN DER AKTE (Bearbeiten, Verwaltung) und derselbe
 * Weg zum Server (`PATCH /auftraege/:id`), keine zweite Liste. Er wird SOFORT
 * gespeichert und nicht mit „Vorbereitung speichern": der Knopf ist
 * gesperrt, solange eine Pflichtangabe fehlt, und am Typ hängt, welche
 * Gewerke unsere sind - das soll nicht an einer fehlenden Breitenangabe
 * hängen. Der Typ der Telekom steht zur Orientierung daneben.
 */
function AuftragstypKarte({ a, ich, auftragId, geaendert }: any) {
  const [listen, setListen] = useState<Auswahllisten | null>(null);
  const [typ, setTyp] = useState<string>(a?.auftragstyp_mds ?? '');
  const [lage, setLage] = useState<{ text: string; gut: boolean } | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  useEffect(() => { auswahllisten().then(setListen).catch(() => {}); }, []);
  useEffect(() => { setTyp(a?.auftragstyp_mds ?? ''); }, [a?.auftragstyp_mds]);
  const darfSchreiben = darf(ich, 'auftrag.schreiben');
  const g = listen?.auftragstyp.find((x) => x.wert === typ)?.gewerke;
  const fremd = g ? [!g.tiefbau && 'Tiefbau', !g.einblasen && 'Einblasen', !g.spleissen && 'Spleißen']
    .filter(Boolean).join(', ') : '';

  async function waehle(neu: string) {
    const vorher = typ;
    setTyp(neu); setFehler(null); setLage(null);
    try {
      await api.auftragAendern(auftragId, { auftragstyp_mds: neu || null });
      setLage({ text: neu ? 'Auftragstyp gespeichert.' : 'Auftragstyp entfernt.', gut: true });
      geaendert?.();
    } catch (f) { setTyp(vorher); setFehler(ausFehler(f)); }
  }

  return (
    <div className="karte">
      <h3>Auftrag</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        <Auswahl name="auftragstyp_mds" titel="Auftragstyp" breite="100%"
                 wert={typ} setze={waehle} gesperrt={!darfSchreiben}
                 optionen={listen?.auftragstyp ?? []} fehler={fehler}
                 leerText="nicht gesetzt"
                 hinweis={!darfSchreiben ? 'Dafür fehlt das Recht „Auftrag schreiben".'
                   : fremd ? `${fremd} zählt bei diesem Typ nicht als offen.` : undefined} />
        {(a?.auftragstyp_tk ?? []).length > 0 && (
          <p className="klein leise" style={{ margin: '4px 0 0' }}>
            Telekom: {(a.auftragstyp_tk as string[]).join(', ')}
          </p>
        )}
        {lage && <p className={'klein ' + (lage.gut ? 'gut' : 'leise')} style={{ margin: '4px 0 0' }}>✓ {lage.text}</p>}
      </div>
    </div>
  );
}
